import { useState, useCallback, FormEvent } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface FormData {
  name: string;
  phone: string;
  plotSize: string;
}

const plotSizeOptions = [
  '1200 Sqft',
  '1500 Sqft',
  '2400 Sqft',
  '3000+ Sqft',
  'Need Consultation',
];

const WHATSAPP_NUMBER = '918861133745';

export default function WhatsAppForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    plotSize: '',
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');

  useScrollReveal('.form-reveal', 0.1);

  const handleChange = useCallback(
    (field: keyof FormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      },
    []
  );

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!consentChecked) {
        setConsentError(true);
        return;
      }
      setConsentError(false);
      setIsSubmitting(true);

      const message = `Hi, I am interested in Confident Auriga.

Name: ${formData.name || 'Not provided'}
Phone: ${formData.phone || 'Not provided'}
Interested Plot Size: ${formData.plotSize || 'Not selected'}

Please share more details.`;

      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

      // Small delay for UX feedback
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitStatus('success');
        window.open(whatsappUrl, '_blank');

        // Reset form after opening
        setTimeout(() => {
          setFormData({ name: '', phone: '', plotSize: '' });
          setConsentChecked(false);
          setSubmitStatus('idle');
        }, 2000);
      }, 600);
    },
    [formData, consentChecked]
  );

  const inputBase =
    'w-full px-4 py-3.5 md:py-4 bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-xl font-body text-sm md:text-base text-white placeholder:text-white/30 focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/10 transition-all duration-500 hover:border-white/20 hover:bg-white/[0.09]';

  const labelBase =
    'block font-body text-xs md:text-sm font-medium text-white/70 mb-2 tracking-wide';

  return (
    <section id="enquiry" className="py-12 md:py-20 bg-[#0F2E24] relative overflow-hidden">
      {/* Glassmorphism ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gold/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-emerald-900/30 rounded-full blur-[100px] pointer-events-none" />

      {/* Subtle dot-grid background */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="section-padding relative z-10">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <div className="form-reveal scroll-reveal">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/[0.08] border border-white/15 rounded-full font-body text-[10px] md:text-xs tracking-[0.2em] uppercase text-white/70 backdrop-blur-md">
                <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" />
                WhatsApp Enquiry
              </span>
            </div>
            <h2 className="form-reveal scroll-reveal font-display text-2xl md:text-4xl lg:text-5xl text-white font-bold mt-4 md:mt-5 leading-tight">
              Get Details Via <span className="text-gradient-gold">WhatsApp</span>
            </h2>
            <p className="form-reveal scroll-reveal font-body text-sm md:text-base text-white/50 mt-3 md:mt-4 leading-relaxed max-w-md mx-auto">
              Fill in your details and we will open WhatsApp with your enquiry pre-filled. Our team will respond instantly.
            </p>
          </div>

          {/* Form Card — Premium Glass */}
          <div className="form-reveal scroll-reveal bg-white/[0.04] backdrop-blur-2xl border border-white/[0.08] rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-2xl shadow-black/20">
            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 md:p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center animate-fade-in">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-whatsapp-line text-emerald-400 text-xl md:text-2xl" />
                </div>
                <p className="font-display text-sm md:text-base text-white font-semibold mb-1">
                  Opening WhatsApp...
                </p>
                <p className="font-body text-xs md:text-sm text-white/60 leading-relaxed">
                  Your enquiry is ready. If WhatsApp did not open, please check your popup blocker.
                </p>
              </div>
            )}

            <form
              id="enquiryForm"
              onSubmit={handleSubmit}
              className="space-y-5 md:space-y-6"
            >
              {/* Full Name */}
              <div>
                <label htmlFor="name" className={labelBase}>
                  Full Name <span className="text-gold">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  className={inputBase}
                />
              </div>

              {/* Phone Number */}
              <div>
                <label htmlFor="phone" className={labelBase}>
                  Phone Number <span className="text-gold">*</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  className={inputBase}
                />
              </div>

              {/* Interested Plot Size */}
              <div>
                <label htmlFor="plotSize" className={labelBase}>
                  Interested Plot Size
                </label>
                <div className="relative">
                  <select
                    id="plotSize"
                    name="plotSize"
                    value={formData.plotSize}
                    onChange={handleChange('plotSize')}
                    className={`${inputBase} appearance-none cursor-pointer pr-10`}
                  >
                    <option value="" disabled className="bg-[#123524] text-white/50">
                      Select plot size
                    </option>
                    {plotSizeOptions.map((opt) => (
                      <option key={opt} value={opt} className="bg-[#123524] text-white">
                        {opt}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none text-lg" />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2.5 px-8 py-4 md:py-5 bg-gold hover:bg-[#c29f5a] text-[#0F2E24] font-body font-semibold text-sm md:text-base tracking-wider uppercase rounded-xl transition-all duration-500 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-gold/20 hover:shadow-xl hover:shadow-gold/30"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-[#0F2E24]/30 border-t-[#0F2E24] rounded-full animate-spin" />
                      Preparing Message...
                    </>
                  ) : (
                    <>
                      <i className="ri-whatsapp-line text-xl" />
                      Send Enquiry on WhatsApp
                    </>
                  )}
                </button>
              </div>

              {/* Consent Checkbox */}
              <div className="pt-1">
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => {
                      setConsentChecked(e.target.checked);
                      if (e.target.checked) setConsentError(false);
                    }}
                    className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-gold focus:ring-gold/30 focus:ring-2 shrink-0 cursor-pointer accent-gold"
                  />
                  <span className="font-body text-[11px] md:text-xs text-white/40 leading-relaxed group-hover:text-white/60 transition-colors duration-300">
                    I authorize Confident Auriga and its representative to contact me with updates and notifications via WhatsApp, SMS, and Call. This will override the registry on DND/NDNC.
                  </span>
                </label>
                {consentError && (
                  <p className="mt-1.5 font-body text-[11px] text-red-300/90 pl-6.5">
                    Please check this box to proceed with your enquiry.
                  </p>
                )}
              </div>

              {/* Trust Note */}
              <p className="text-center font-body text-[10px] md:text-xs text-white/30 leading-relaxed max-w-sm mx-auto">
                By submitting, WhatsApp will open with your enquiry pre-filled. No data is stored on our servers.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}